import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import { geolocationHeader } from '../lib/geolocation';

export type BankAccountType = 'CLABE' | 'ABA' | 'CPA';

export interface ClientBankAccount {
  id: string;
  account_type: BankAccountType;
  country_code: string;
  currency_code: string;
  account_number_last4: string;
  holder_name: string;
  nickname: string | null;
  is_active: boolean;
}

export interface ClientBankAccountFull extends ClientBankAccount {
  /** Full plaintext — staff-only sender-lookup response. */
  account_number: string;
  /** The holder's user id (NOT the account `id`). Manual attribution uses it
   *  to pre-select the client. */
  user_id: string;
}

export interface CreateBankAccountPayload {
  account_type: BankAccountType;
  country_code: string;
  currency_code: string;
  account_number: string;
  holder_name: string;
  nickname?: string | null;
  /** Fresh single-use TOTP step-up code. The FIRST account a client registers needs none; a
   *  second or later one does (S-27). A code that is sent is always checked. */
  mfa_code?: string;
}

class BankAccountsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listForClient(clientId: string): Promise<ClientBankAccount[]> {
    const res = await this.api.get<ClientBankAccount[]>(
      `/api/v1/clients/${clientId}/bank-accounts`,
    );
    return res.data;
  }

  async listMine(): Promise<ClientBankAccount[]> {
    const id = await authService.getMyId();
    return this.listForClient(id);
  }

  async create(
    clientId: string,
    body: CreateBankAccountPayload,
  ): Promise<ClientBankAccount> {
    // Adding a withdrawal destination is gated on precise browser location.
    const headers = await geolocationHeader();
    const res = await this.api.post<ClientBankAccount>(
      `/api/v1/clients/${clientId}/bank-accounts`,
      body,
      { headers },
    );
    return res.data;
  }

  async createMine(body: CreateBankAccountPayload): Promise<ClientBankAccount> {
    const id = await authService.getMyId();
    return this.create(id, body);
  }

  async deactivate(clientId: string, accountId: string, mfaCode: string): Promise<void> {
    // DELETE carries a body: the step-up code travels in the request
    // body (never the URL) so it stays out of logs and browser history.
    // Removing a withdrawal destination is gated on precise browser location.
    const headers = await geolocationHeader();
    await this.api.delete(
      `/api/v1/clients/${clientId}/bank-accounts/${accountId}`,
      { data: { mfa_code: mfaCode }, headers },
    );
  }

  /** Staff-only — submit the plaintext inbound sender account and get
   *  back the matching client_bank_accounts rows (with full plaintext
   *  for the manual-attribution page). */
  async senderLookup(accountNumber: string): Promise<ClientBankAccountFull[]> {
    const res = await this.api.post<ClientBankAccountFull[]>(
      '/api/v1/ops/client-bank-accounts/sender-lookup',
      { account_number: accountNumber },
    );
    return res.data;
  }
}

export default new BankAccountsService();
